<template>
    <CModal :visible="visible" alignment="center" size="lg" @close="handleClose">
        <CModalHeader>
            <CModalTitle>Import Screening Data</CModalTitle>
        </CModalHeader>
        <CModalBody>
            <template v-if="!importing">
                <p class="small mb-2 text-muted">One file per type, one calendar month per import.</p>
                <div class="mb-3">
                    <FileUpload
                        ref="fileUploadRef"
                        v-model="allFileIds"
                        :accepted-file-types="['text/csv', 'text/plain', 'application/vnd.ms-excel', 'application/octet-stream', '.systemevent']"
                        :file-validate-type-detect-type="detectUploadMimeType"
                        :url="route('eds-operations.upload')"
                        drop-area-label="Drop files here (Bag Data, BNA, Machine Reference, System Events)"
                        drop-area-hint="Max 256 MB per file."
                        @upload-completed="syncDetectedFiles"
                    />
                </div>
                <div v-if="detectedFiles.length > 0" class="mb-3">
                    <strong class="small">Detected files:</strong>
                    <div v-for="file in detectedFiles" :key="file.id" class="d-flex align-items-center mt-1 gap-2">
                        <icon v-if="file.type !== 'unknown'" class="text-success" icon="cil-check-circle" />
                        <icon v-else class="text-warning" icon="cil-warning" />
                        <span class="small">{{ file.name }}</span>
                        <span class="small text-muted">&rarr;</span>
                        <CFormSelect v-if="file.type === 'unknown'" v-model="file.type" class="form-select-sm" style="width: auto">
                            <option value="unknown">Select type...</option>
                            <option value="bag_data">Bag Data</option>
                            <option value="bna">BNA</option>
                            <option value="system_events">System Events</option>
                            <option value="machine_ref">Machine Reference</option>
                        </CFormSelect>
                        <CBadge v-else :color="typeColor(file.type)">{{ typeLabel(file.type) }}</CBadge>
                    </div>
                </div>
                <div v-if="duplicatedTypeLabels.length > 0" class="small mb-3 text-danger">
                    Multiple {{ duplicatedTypeLabels.join(' and ') }} files — only one per type per import.
                </div>
                <div v-if="detectedFiles.length > 0 && formatMode === 'invalid'" class="small mb-3 text-danger">
                    BNA is part of the MAN format only — it can't be combined with Machine Reference (ODB folds BNA into Bag Data).
                </div>
                <div
                    v-if="detectedFiles.length > 0 && formatMode === 'man' && !hasBagData && !hasBna && !hasSystemEvents"
                    class="small mb-3 text-danger"
                >
                    Provide at least one of Bag Data, BNA, or System Events.
                </div>
                <div v-if="detectedFiles.length > 0 && formatMode === 'odb' && !hasSystemEvents && !hasBagData" class="small mb-3 text-danger">
                    Provide System Events, Bag Data, or both alongside Machine Reference.
                </div>
                <div class="row mb-3">
                    <div class="col-md-6">
                        <CFormLabel for="op_hours_start">Op Hours Start</CFormLabel>
                        <CFormInput id="op_hours_start" v-model.number="opHoursStart" :disabled="fullDayOpWindow" max="23" min="0" type="number" />
                    </div>
                    <div class="col-md-6">
                        <CFormLabel for="op_hours_end">Op Hours End</CFormLabel>
                        <CFormInput id="op_hours_end" v-model.number="opHoursEnd" :disabled="fullDayOpWindow" max="24" min="0" type="number" />
                    </div>
                </div>
                <div class="d-flex align-items-center mb-3 gap-2">
                    <Switch id="smiths-full-day-op-window" v-model="fullDayOpWindow" />
                    <span class="small text-muted">24 hours</span>
                </div>
                <div v-if="error" class="small mt-2 text-danger">{{ error }}</div>
            </template>

            <template v-else>
                <div class="py-4 text-center">
                    <CSpinner v-if="importStatus === 'pending' || importStatus === 'started'" class="mb-3" />
                    <icon v-else-if="importStatus === 'completed'" class="text-success mb-3" icon="cil-check-circle" size="3xl" />
                    <icon v-else-if="importStatus === 'failed'" class="mb-3 text-danger" icon="cil-x-circle" size="3xl" />

                    <p v-if="importStatus === 'pending'" class="text-muted">Queued for processing...</p>
                    <p v-else-if="importStatus === 'started'" class="text-muted">Processing CSV files...</p>
                    <p v-else-if="importStatus === 'completed'" class="text-success">
                        Import complete: {{ importResult?.days_imported }} days, {{ importResult?.total_bags?.toLocaleString() }} bags
                        <template v-if="importResult?.events_imported">, {{ importResult?.events_imported }} event days</template>
                    </p>
                    <p v-else-if="importStatus === 'failed'" class="text-danger">
                        Import failed: {{ importResult?.error_message || 'Unknown error' }}
                    </p>
                </div>
            </template>
        </CModalBody>
        <CModalFooter>
            <CButton color="secondary" @click="handleClose">
                {{ importing && importStatus === 'completed' ? 'Close' : 'Cancel' }}
            </CButton>
            <CButton v-if="!importing" :disabled="!canSubmit || submitting" color="primary" @click="submit">
                <CSpinner v-if="submitting" size="sm" />
                Import
            </CButton>
        </CModalFooter>
    </CModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import {
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CModalFooter,
    CButton,
    CFormLabel,
    CFormInput,
    CFormSelect,
    CSpinner,
    CBadge,
} from '@coreui/vue';
import FileUpload from '@/Components/FileUpload.vue';
import Switch from '@/Components/Switch.vue';
import { $http } from '@/bootstrap.js';
import { router } from '@inertiajs/vue3';

const props = defineProps({
    visible: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const machineType = ref('eds');
const opHoursStart = ref(3);
const opHoursEnd = ref(23);
const fullDayOpWindow = ref(false);
const allFileIds = ref([]);
const detectedFiles = ref([]);
const fileUploadRef = ref(null);
const submitting = ref(false);
const importing = ref(false);
const importStatus = ref(null);
const importResult = ref(null);
const error = ref(null);
let pollInterval = null;

// FilePond validates uploads by MIME type; .systemevent files come through
// with an empty string. This detector both passes FilePond's plugin and feeds
// the upload-intent POST so the server's required content_type rule passes.
function detectUploadMimeType(source, type) {
    return new Promise((resolve) => {
        if (type) {
            resolve(type);
            return;
        }
        const name = source?.name?.toLowerCase() ?? '';
        if (name.endsWith('.systemevent') || name.includes('.systemevent.')) {
            resolve('application/octet-stream');
            return;
        }
        resolve(type);
    });
}

function detectFileType(filename) {
    const lower = filename.toLowerCase();
    if (lower === 'machine.csv' || /(^|[^a-z])machine\.csv$/.test(lower)) return 'machine_ref';
    if (lower.includes('.systemevent')) return 'system_events';
    if (lower.startsWith('baginfo')) return 'bag_data';
    if (lower.includes('bag data')) return 'bag_data';
    if (lower.includes('bna') || lower.startsWith('host.')) return 'bna';
    if (lower.includes('system events')) return 'system_events';
    return 'unknown';
}

function typeLabel(type) {
    return { bag_data: 'Bag Data', bna: 'BNA', system_events: 'System Events', machine_ref: 'Machine Reference' }[type] ?? 'Unknown';
}

function typeColor(type) {
    return 'primary';
}

function syncDetectedFiles() {
    const pondFiles = fileUploadRef.value?.$refs?.pond?.getFiles() ?? [];
    const previousType = new Map(detectedFiles.value.map((d) => [d.id, d.type]));
    detectedFiles.value = pondFiles
        .filter((f) => f.serverId)
        .map((f) => ({
            id: f.serverId,
            name: f.filename,
            // Preserve a manually-chosen type across re-syncs (e.g. unknown → bag_data).
            type: previousType.get(f.serverId) ?? detectFileType(f.filename),
        }));
}

const hasBagData = computed(() => detectedFiles.value.some((f) => f.type === 'bag_data'));
const hasBna = computed(() => detectedFiles.value.some((f) => f.type === 'bna'));
const hasMachineRef = computed(() => detectedFiles.value.some((f) => f.type === 'machine_ref'));
const hasSystemEvents = computed(() => detectedFiles.value.some((f) => f.type === 'system_events'));

// Each backend slot accepts a single file. fileIdByType picks the first
// match silently, so duplicates would be dropped without warning — surface
// them instead and block submit.
const duplicatedTypeLabels = computed(() => {
    const counts = {};
    for (const file of detectedFiles.value) {
        if (file.type === 'unknown') continue;
        counts[file.type] = (counts[file.type] ?? 0) + 1;
    }
    return Object.entries(counts)
        .filter(([, count]) => count > 1)
        .map(([type]) => typeLabel(type));
});

// Two valid families, each with flexible content:
// - MAN: any combination of bag_data, bna, system_events (no machine_ref).
// - ODB: machine_ref + at least one of system_events or bag_data.
// BNA is MAN-only — pairing it with machine_ref is a mistake.
const formatMode = computed(() => {
    if (hasMachineRef.value && hasBna.value) return 'invalid';
    if (hasMachineRef.value) return 'odb';
    return 'man';
});

const canSubmit = computed(() => {
    if (detectedFiles.value.some((f) => f.type === 'unknown')) return false;
    if (duplicatedTypeLabels.value.length > 0) return false;
    if (formatMode.value === 'invalid') return false;
    if (formatMode.value === 'odb') {
        return hasMachineRef.value && (hasSystemEvents.value || hasBagData.value);
    }
    return hasBagData.value || hasBna.value || hasSystemEvents.value;
});

function fileIdByType(type) {
    return detectedFiles.value.find((f) => f.type === type)?.id ?? null;
}

async function submit() {
    error.value = null;
    submitting.value = true;

    try {
        const response = await $http.post(route('eds-operations.import'), {
            machine_type: machineType.value,
            bag_data_temp_file_id: fileIdByType('bag_data'),
            bna_temp_file_id: fileIdByType('bna'),
            system_events_temp_file_id: fileIdByType('system_events'),
            machine_csv_temp_file_id: fileIdByType('machine_ref'),
            op_hours_start: opHoursStart.value,
            op_hours_end: opHoursEnd.value,
        });

        importing.value = true;
        importStatus.value = 'pending';
        importResult.value = response.data;

        startPolling(response.data.id);
    } catch (err) {
        error.value = err.response?.data?.message || 'Failed to start import.';
    } finally {
        submitting.value = false;
    }
}

function startPolling(importId) {
    pollInterval = setInterval(async () => {
        try {
            const response = await $http.get(route('eds-operations.import.status', { import: importId }));
            importResult.value = response.data;
            importStatus.value = response.data.status;

            if (response.data.status === 'completed' || response.data.status === 'failed') {
                clearInterval(pollInterval);
                pollInterval = null;

                if (response.data.status === 'completed' && response.data.unit_identifier && response.data.month) {
                    setTimeout(() => {
                        router.visit(
                            route('eds-operations.index', {
                                unit_identifier: response.data.unit_identifier,
                                month: response.data.month,
                            }),
                        );
                        emit('close');
                    }, 1500);
                }
            }
        } catch {
            clearInterval(pollInterval);
            pollInterval = null;
            importStatus.value = 'failed';
        }
    }, 2000);
}

function handleClose() {
    if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
    }
    importing.value = false;
    importStatus.value = null;
    importResult.value = null;
    error.value = null;
    detectedFiles.value = [];
    allFileIds.value = [];
    emit('close');
}

// FileUpload emits `update:model-value` on both upload completion and file
// removal — keep detectedFiles aligned so the duplicate-type warning clears
// when the user removes the offending file.
watch(allFileIds, syncDetectedFiles, { deep: true });

watch(
    () => props.visible,
    (val) => {
        if (!val && pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
        }
    },
);

watch(
    fullDayOpWindow,
    (enabled) => {
        if (!enabled) {
            return;
        }

        opHoursStart.value = 0;
        opHoursEnd.value = 24;
    },
    { immediate: true },
);
</script>
